import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import ReactModal from 'react-modal';

import classNames from 'classnames';

import { rootStyleVariables } from '../../constants/rootStyleVariables';
import { ModalLogoContainer } from '../../containers/ModalLogoContainer/ModalLogoContainer';
import { keyboardHooks } from '../../hooks/keyboardHooks/keyboardHooks';
import { DisableBodyScroll } from '../DisableBodyScroll/DisableBodyScroll';
import { Close, Arrow } from '../icons';
import { CloseThickness } from '../icons/Close/Close';
import styles from './Modal.module.css';

const messages = defineMessages({
  modalLogoAlt: {
    id: 'src.components.Modal.modalLogoAlt',
    defaultMessage: 'modal logo',
  },
});

export type Animation = {
  base: string;
  hide: string;
  show: string;
};

export type LayoutAnimation = {
  desktop: Animation;
  mobile: Animation;
};

export type AnimationType =
  'opacity' | 'fromLeftToRight' | 'fromDownToTop' | 'fromOuterToRight' | 'noOpacity';

export const Animations: {[key in AnimationType]: Animation } = {
  opacity: {
    base: styles.animationOpacityBase,
    show: styles.animationOpacityShow,
    hide: styles.animationOpacityHide,
  },
  fromOuterToRight: {
    base: styles.animationFromOuterToRightBase,
    show: styles.animationFromOuterToRightShow,
    hide: styles.animationFromOuterToRightHide,
  },
  fromLeftToRight: {
    base: styles.animationFromLeftToRightBase,
    show: styles.animationFromLeftToRightShow,
    hide: styles.animationFromLeftToRightHide,
  },
  fromDownToTop: {
    base: styles.animationFromDownToTopBase,
    show: styles.animationFromDownToTopShow,
    hide: styles.animationFromDownToTopHide,
  },
  noOpacity: {
    base: styles.animationNoOpacityBase,
    show: styles.animationNoOpacityShow,
    hide: styles.animationNoOpacityHide,
  },
};

export const defaultAnimation: LayoutAnimation = {
  desktop: Animations.fromLeftToRight,
  mobile: Animations.fromDownToTop,
};

export const hooks = {
  useHandleClose(
    onClose: () => void,
    loading: boolean,
  ) {
    return React.useCallback(() => {
      if (!loading) {
        return onClose();
      }
    }, [onClose, loading]);
  },
};

export type Props = {
  animation?: LayoutAnimation;
  backBtnClassName?: string;
  children: JSX.Element | string;
  closeBtnClassName?: string;
  closeBtnColor?: string;
  dragHandle?: boolean;
  fixedFooter?: JSX.Element | string;
  fixedFooterClassName?: string;
  hasBackBtn?: boolean;
  hasBackdrop?: boolean;
  hasLogo?: boolean;
  hasTopShadow?: boolean;
  header?: JSX.Element | string;
  isHideCloseButton?: boolean;
  isMobileLayout: boolean;
  isOpen: boolean;
  loading?: boolean;
  logoClassName?: string;
  modalContentClassName?: string;
  modalContentStyle?: React.CSSProperties;

  modalContentWrapperClassName?: {
    afterOpen?: string;
    base?: string;
    beforeClose?: string;
  };
  noOpacityChange?: boolean;
  onBack?: () => void;
  onClose: () => void;
  overlayClassName?: string;
  shouldCloseOnEsc?: boolean;
};

export const Modal = React.memo((props: Props) => {
  const {
    isOpen,
    animation = defaultAnimation,
    children,
    header,
    overlayClassName,
    closeBtnClassName,
    modalContentWrapperClassName,
    modalContentClassName,
    backBtnClassName,
    logoClassName,
    hasBackBtn,
    hasBackdrop = true,
    hasLogo,
    hasTopShadow,
    fixedFooter,
    fixedFooterClassName,
    loading,
    onClose,
    onBack,
    isMobileLayout,
    shouldCloseOnEsc = false,
    isHideCloseButton,
    modalContentStyle,
    closeBtnColor,
    dragHandle,
    noOpacityChange,
  } = props;
  const intl = useIntl();

  const animationStyles = noOpacityChange
    ? { desktop: Animations.noOpacity, mobile: Animations.noOpacity }
    : animation;

  const handleClose = hooks.useHandleClose(onClose, !!loading);

  keyboardHooks.useEscape(shouldCloseOnEsc && isOpen, onClose);

  return (
    <ReactModal
      isOpen={isOpen}
      closeTimeoutMS={300}
      onRequestClose={handleClose}
      shouldFocusAfterRender={shouldCloseOnEsc}
      overlayClassName={{
        base: classNames(styles.overlay, overlayClassName,
          rootStyleVariables[CONFIG.applicationSiteGroup]),
        afterOpen: styles.overlayAfterOpen,
        beforeClose: styles.overlayBeforeClose,
      }}
      className={{
        base: classNames(
          styles.modalContentWrapper,
          modalContentWrapperClassName?.base,
          isMobileLayout ? animationStyles.mobile.base : animationStyles.desktop.base,
        ),
        afterOpen:
          classNames(isMobileLayout ? animationStyles.mobile.show : animationStyles.desktop.show),
        beforeClose:
          classNames(isMobileLayout ? animationStyles.mobile.hide : animationStyles.desktop.hide),
      }}
      style={{
        content: modalContentStyle,
      }}
      contentElement={(props, children) => (
        <>
          <DisableBodyScroll />
          {hasBackdrop && <div className={styles.backdrop} />}
          {hasTopShadow && <div className={classNames({ [styles.topShadow]: hasTopShadow })} />}

          <div {...props}>
            <header className={classNames(styles.header)}>
              {hasLogo && !hasBackBtn && (
                <ModalLogoContainer
                  alt={intl.formatMessage(messages.modalLogoAlt)}
                  className={classNames(styles.logo, logoClassName)}
                />
              )}

              {hasBackBtn && (
                <Arrow
                  variant='left'
                  fill='black'
                  className={classNames(styles.backBtn, backBtnClassName)}
                  onClick={onBack}
                />
              )}

              {header}
              {!isHideCloseButton && !dragHandle && (
                <Close
                  className={classNames(styles.closeBtn, closeBtnClassName)}
                  onClick={handleClose}
                  thickness={CloseThickness.Thin}
                  color={closeBtnColor}
                />
              )}
              {dragHandle && (
                <div className={styles.modalDragHandle} onClick={handleClose}>
                  <div className={styles.modalDragHandleLine} />
                </div>
              )}
            </header>

            <div className={classNames(styles.content, modalContentClassName)}>
              {children}
            </div>

            {fixedFooter && (
              <footer className={classNames(styles.fixedFooter, fixedFooterClassName)}>
                {fixedFooter}
              </footer>
            )}
          </div>
        </>
      )}
    >
      {children}
    </ReactModal>
  );
});

Modal.displayName = 'Modal';
